// ============================================================================
// CHITTI AYEE HAI — MASTER SITE CONFIGURATION
// ----------------------------------------------------------------------------
// This ONE file controls the entire website. Edit values here and every
// section, button, share message and metadata field updates automatically.
// The same data is mirrored in the backend (GET /api/config) for the AI agents.
//
//   bookMyShowUrl     -> every ticket button on the site
//   posterAsset       -> path to the official poster (place file in
//                        frontend/public, e.g. "/poster.jpg") — swaps into the hero
//   ga4MeasurementId  -> paste your GA4 "G-XXXXXXX" ID to activate analytics
// ============================================================================

export const SITE = {
  eventName: "Chitti Ayee Hai",
  eventNameCaps: "CHITTI AYEE HAI",
  urdu: "چٹی آئی ہے",
  tagline: "A Soulful Evening of Ghazal, Qawwali & Sufi Music",
  taglineShort: "Ghazal • Qawwali • Sufi",
  city: "Bengaluru",
  domain: "https://chittiayeehai.com",

  // ---- OFFICIAL LINKS ----
  bookMyShowUrl:
    "https://in.bookmyshow.com/events/chitti-ayee-hai-qawwali-sufi-music-bengaluru/ET00508906",
  instagramUrl: "https://www.instagram.com/chitti_ayee_hai",
  instagramHandle: "@chitti_ayee_hai",
  facebookUrl: "https://www.facebook.com/share/1GASTvfYQT/?mibextid=wwXIfr",
  whatsappChannelUrl: "https://whatsapp.com/channel/0029Vb7zmHDDTkK7IUqM2x3t",
  youtubeUrl: "https://www.youtube.com/@chittiayeehai",
  whatsappNumber: "919353952967", // enquiries — from the official poster (+91 93539 52967)
  contactEmail: "mehfil@chittiayeehai.com",
  privacyUrl: "/privacy",
  termsUrl: "/terms",

  // ---- CHITTI AI (WhatsApp) — number + pre-filled message used by every
  // "Ask Chitti" entry point on the site. This is the number the Chitti AI
  // (WATI) agent is actually integrated on — deliberately kept separate from
  // the general `whatsappNumber` above (used for footer/social "enquiries"
  // links and the /artist stall-enquiry link, which are unchanged and stay
  // on the original number). No backend/WATI config change — the agent is
  // already live on this number today. ----
  chittiWhatsappNumber: "917984127393", // Chitti AI — the number the WATI agent is integrated on (+91 79841 27393)
  chittiPrefillMessage: "Hi Chitti, I want to know about Chitti Ayee Hai tickets.",

  // ---- ANALYTICS (events are pre-wired; paste IDs to activate) ----
  ga4MeasurementId: "", // TODO: "G-XXXXXXXXXX"
  googleAdsConversionId: "AW-18404460770",
  googleAdsConversionLabel: "", // TODO: paste the conversion label when created in Google Ads
  metaPixelId: "", // TODO

  // ---- POSTER (official artwork) ----
  posterAsset: "/poster.jpg",

  // ---- BRAND (official logo — change these paths once to update everywhere) ----
  brand: {
    logo: "/brand/logo.webp", // full artwork (1200px)
    logoSmall: "/brand/logo-small.webp", // header/footer (520px)
    logoAlt: "Chitti Ayee Hai — official logo",
  },

  // ---- SCHEDULE (verified from the official BookMyShow listing) ----
  date: "Saturday, 19 September 2026",
  dateIso: "2026-09-19",
  schedule: {
    exhibition: "5:00 PM onwards",
    boxOffice: "6:00 PM onwards",
    entry: "6:30 PM onwards",
    show: "7:00 PM sharp",
    duration: "3 hours",
  },

  // ---- TICKETING (verified) ----
  priceFrom: 599,
  currency: "INR",
  listingStatus: "Filling Fast",
  interestedCount: "1.2K",

  // ---- EVENT FACTS (verified) ----
  ageLimit: "10+",
  languages: ["Hindi", "Urdu", "Kannada"],
  seatingNote: "Seating within each ticket category is first-come, first-served.",
  parking: "Parking available at the venue.",
  organizer: "House of Bengaluru",

  // ---- VENUE ----
  venue: {
    name: "Dr. B.R. Ambedkar Bhavan",
    area: "Vasanth Nagar",
    city: "Bengaluru",
    capacity: "Approximately 1,254 seats",
    mapsUrl: "https://maps.app.goo.gl/6yZ3E1EGpDXgWviF7", // official Google Maps venue link
    // Real photo of the venue's entrance/facade — a genuine photograph of Dr.
    // B.R. Ambedkar Bhavan, confirmed by its own signage in the shot (not a
    // stock or AI-generated image). Tall/portrait source, so the frame's
    // object-position favors the upper facade + name board rather than a
    // dead-center crop that would cut off the signage.
    photo: "/venue/ambedkar-bhavan.jpg",
    hasPhoto: true,
  },

  // ---- ARTISTS (as officially listed on BookMyShow) ----
  // NOTE: BookMyShow's listing showed "Dr. Kaleem Pasha" and "Mr. Kamal Raj" as two
  // separate names. His own official website states his full name as "Kaleem Pasha
  // (also known as Kamal Raj)" — confirming these are ONE person, credited under two
  // name variants. Kept as a single verified entry to avoid implying two performers.
  artists: [
    {
      name: "Dr. Kaleem Pasha",
      source: "As listed on BookMyShow (also credited there as Mr. Kamal Raj)",
      website: "https://www.kaleempashaofficial.com/",
    },
  ],

  // ---- THE ARTIST — full profile (verified from his official website + national
  // press coverage of his career; see `sourcesNote` below). Powers the homepage
  // artist section AND the dedicated /artist page. Do not add claims here that are
  // not traceable to kaleempashaofficial.com or the press profile cited below. ----
  artist: {
    fullName: "Dr. Kaleem Pasha",
    stageName: "Kamal",
    akaNote: "Also credited professionally as Kamal Raj",
    roles: [
      "Actor",
      "Singer",
      "Producer",
      "Filmmaker",
      "Author",
      "Poet",
      "Lyricist",
      "Composer",
      "Entrepreneur",
      "Motivational Speaker",
    ],
    // Real solo portrait, supplied directly by the team (formal, direct gaze —
    // used as-is, matching the site's existing crop treatment) and placed at
    // the path below. Flipped to true now that the file actually exists.
    hasPhoto: true,
    photo: "/artist/kaleem-pasha.jpg",
    website: "https://www.kaleempashaofficial.com/",
    websiteLabel: "Explore His Full Archive",
    urdu: "کلیم پاشا",
    tagline: "Actor. Singer. Producer. A voice carried by poetry.",
    intro:
      "Credited on screen as Kamal, Dr. Kaleem Pasha has spent his career moving between cinema, poetry and music — an actor who writes, a singer who produces, and a storyteller whose work has been profiled across national media. He joins Chitti Ayee Hai not as a guest performer, but as one of its guiding artistic spirits.",
    // Verbatim from his official website's About page (kaleempashaofficial.com/about.html) —
    // do not edit the wording, only remove it entirely if it's ever contested.
    quote:
      "When I look back at my life, I don't ask Almighty Allah for anything more. Every step of my journey has been filled with blessings beyond imagination.",
    highlights: [
      {
        title: "Profiled in National Media",
        text: "His career has been featured by national outlets including The Wire and The Tribune, spotlighting his work across acting, writing and music.",
      },
      {
        title: "Published Author",
        text: "Author of nine books spanning poetry, memoir and motivational writing — from Zindagi Ki Dastan to 1000 Shayariyaan.",
      },
      {
        title: "Actor, Producer & Studio Founder",
        text: "A Kannada cinema actor who has also built his own post-production studio, offering AI dubbing, VFX and green-screen production.",
      },
      {
        title: "A Voice for Ghazal & Sufi Music",
        text: "A trained sarod player recognised for renditions that carry forward the spirit of ghazal legend Pankaj Udhas.",
      },
      {
        title: "Recognitions",
        text: "As listed on his official website, honours include the Maharashtra Gaurav Samman Award, Natural Star Award and Indian Iconic Award recognition, among others.",
      },
    ],
    films: {
      intro:
        "His acting career includes a widely noted breakthrough in Kannada cinema, alongside production and studio ventures built around his own storytelling.",
      items: [
        {
          title: "The Suit",
          role: "Actor",
          note: "A Kannada film breakthrough that drew critical attention to his acting.",
        },
        {
          title: "Joker",
          role: "Actor",
          note: "An upcoming Kannada feature, described on his official website as among his most anticipated projects.",
          upcoming: true,
        },
      ],
      ventures: [
        "Founder of a post-production studio offering AI dubbing, VFX and green-screen production.",
        "Building an OTT streaming platform to support independent creators.",
        "Runs a publishing and media production house behind his books and music.",
        "Operates a 50-apartment luxury rental hospitality business.",
      ],
    },
    books: {
      intro:
        "A published author across poetry, memoir and motivational writing, with nine titles carrying his name.",
      items: [
        "Zindagi Ki Dastan",
        "1000 Shayariyaan — Jazbaat, Dil aur Dard",
        "Jazbaat Se",
        "Tippu Sultan — The Tiger of Vision, Valor & Virtue",
        "To My Kids (Volumes I & II)",
        "A Journey of Success in Life",
        "Marketing and Success — The Only Religion",
        "Marketing Icons of India — Modi & Shah Rukh Khan",
        "God's Universal Business School",
      ],
    },
    music: {
      intro: "Long before Chitti Ayee Hai, his voice found its home in ghazal and playback singing.",
      items: [
        {
          title: "Ambari",
          note: "A Kannada song written for the Mysuru Dasara celebrations, written and directed by Dr. Nagendra Prasad.",
        },
      ],
      note: "He is known for renditions that echo the timeless style of ghazal maestro Pankaj Udhas, and trained as a sarod player.",
    },
    // As stated on his official website's About page: he "has met and worked with"
    // these names — kept as that exact framing (not "co-starred with" or implying
    // formal billing together), since that is what the source actually claims.
    //
    // Photos below are NOT photos of Kaleem Pasha with these people — they are
    // portraits used purely to visually represent the named legend on their own
    // card. Each is a real, Wikimedia Commons–hosted photograph under a
    // Creative Commons Attribution 3.0 license (verified on-page before use).
    // Full source/license/attribution record:
    // claude/chitti-ayee-hai-artist-legends-photo-sourcing-blocked-status.md
    // in the project. A person with no verified photo yet keeps photo: null,
    // which renders the existing star/arch placeholder — never an unverified image.
    legends: {
      intro:
        "Over the course of his career, he has met and worked with major names across Indian film and music.",
      items: [
        { name: "Lata Mangeshkar", note: "Launched his book \"Zindagi Ki Dastan.\"", photo: "/artist/legends/legend-lata-mangeshkar.jpg" },
        { name: "Dharmendra", note: "", photo: "/artist/legends/legend-dharmendra.jpg" },
        { name: "Shah Rukh Khan", note: "", photo: "/artist/legends/legend-shah-rukh-khan.jpg" },
        { name: "Salman Khan", note: "", photo: "/artist/legends/legend-salman-khan.jpg" },
        { name: "Sushmita Sen", note: "", photo: "/artist/legends/legend-sushmita-sen.jpg" },
        { name: "Allu Arjun", note: "", photo: "/artist/legends/legend-allu-arjun.jpg" },
      ],
    },
    // Real stage/performance photos supplied directly by the team, curated
    // from a larger set to four distinct events/looks rather than near-
    // duplicate shots of the same moment. Captions describe only what is
    // visibly true in each photo — no venue, event name, or other performer
    // is claimed unless it was independently confirmed.
    gallery: {
      intro: "Moments from his career — on stage, on set, and behind the mic.",
      items: [
        { src: "/artist/gallery/kaleem-stage-01.jpg", caption: "Live in performance on stage" },
        { src: "/artist/gallery/kaleem-stage-02.jpg", caption: "Performing at a felicitation event" },
        { src: "/artist/gallery/kaleem-stage-03.jpg", caption: "On stage with the ensemble" },
        { src: "/artist/gallery/kaleem-stage-04.jpg", caption: "In performance under stage lights" },
      ],
    },
    // Verified directly from his official YouTube channel, @KaleempashaAudio1
    // (checked live 4 Sept 2026 — subscriber/view counts will drift over time, treat
    // as "as of" figures, not exact). Video IDs confirmed against the channel's own
    // page data. Do not add a video here that wasn't confirmed the same way.
    videos: {
      channelName: "Kaleem Pasha Audio",
      channelHandle: "@KaleempashaAudio1",
      channelUrl: "https://www.youtube.com/@KaleempashaAudio1",
      subscribersLabel: "5.6K+",
      intro:
        "Beyond the stage, his original Hindi and Kannada music videos — written, and often directed by him — have found a real audience on his own YouTube channel.",
      items: [
        {
          id: "dF_ycq22RBw",
          title: "Akhari Sajda",
          language: "Hindi",
          viewsLabel: "105K+ views",
        },
        {
          id: "2Upn4W6QVrw",
          title: "Sajda",
          language: "Hindi",
          viewsLabel: "68K+ views",
        },
        {
          id: "8--abWL9LyA",
          title: "Tere Bina",
          language: "Kannada",
          viewsLabel: "60K+ views",
        },
        {
          id: "JcAM4YzOaVQ",
          title: "Ruksana",
          language: "Kannada",
          viewsLabel: "59K+ views",
        },
      ],
    },
    // Hard, first-party-verifiable numbers only (no self-reported figures like the
    // "300+ awards" claim, which stays hedged in prose elsewhere on the page, not
    // presented here as a headline stat).
    stats: [
      { value: "9", label: "Published Books" },
      { value: "5.6K+", label: "YouTube Subscribers" },
      { value: "300K+", label: "Music Video Views" },
    ],
    timeline: [
      {
        year: "2018",
        title: "Passionate About Entertaining",
        text: "His acting and poetry begin capturing the spirit of every stage of life.",
      },
      {
        year: "2019",
        title: "A Growing Following",
        text: "His work reaches a wide, young audience across Karnataka.",
      },
      {
        year: "2020",
        title: "Kaleem's Poetry Truly Inspires",
        text: "His poetry and shayari reach a national readership.",
      },
      {
        year: "2023",
        title: "Building Beyond the Screen",
        text: "Expands into publishing, post-production and entrepreneurship alongside his acting career.",
      },
      {
        year: "2026",
        title: "Chitti Ayee Hai, Bengaluru",
        text: "Headlines a soulful evening of Ghazal, Qawwali and Sufi music at Dr. B.R. Ambedkar Bhavan.",
      },
    ],
    connection:
      "Chitti Ayee Hai — “the letter has arrived” — is built around the same tradition Dr. Kaleem Pasha has spent his career inside: poetry sung aloud, emotion carried by melody, a room full of strangers feeling one thing together. His presence turns the evening from a concert into a mehfil.",
    sourcesNote:
      "Career details are drawn from his official website (kaleempashaofficial.com — Home, About and Gallery pages), his official YouTube channel (@KaleempashaAudio1), and his published national press profile. Nothing here states or implies any work in the Gujarati film industry — that has not been found or confirmed on any source checked, and is deliberately left out pending direct confirmation from the artist/team.",
  },

  // ---- CHIEF GUEST (verified) ----
  // The ONLY verified facts: Usha Mangeshkar is the Chief Guest and is
  // expected to perform two songs at Chitti Ayee Hai. Deliberately no
  // biography, awards, dates, relationships, quotes, career statistics or
  // claims about specific songs/performance history — none of that has been
  // confirmed for this event, so none of it is stated here.
  // hasPhoto/photo stay false/null until an approved, verified photograph of
  // her (for this event) is supplied — never substitute an unverified,
  // downloaded or AI-generated image. Flip these once a real photo exists,
  // same pattern used for the artist/legends/venue photos above.
  chiefGuest: {
    name: "Usha Mangeshkar",
    role: "Chief Guest",
    presenceLine: "A special presence at Chitti Ayee Hai",
    performanceNote: "Expected to perform two special songs",
    // Real, identity-confirmed photograph — Bollywood Hungama, 2007, "Usha
    // Mangeshkar at an album release party for her sister Lata Mangeshkar."
    // Verified directly on its Wikimedia Commons file page (structured data
    // "depicts: Usha Mangeshkar"; SHA-1 checksum matched byte-for-byte before
    // use). Licensed CC BY 3.0 Unported, VRT-confirmed permission (ticket
    // #2008030310010794) — same VRT ticket/license already used for several
    // verified photos on the Artist page. Full record: see
    // claude/chitti-ayee-hai-usha-mangeshkar-photo-sourcing-record.md in the
    // project. This flag also drives the homepage Chapter 04 (ChiefGuest.jsx)
    // section, which switches from the medallion to this same photo.
    hasPhoto: true,
    photo: "/usha-mangeshkar/usha-mangeshkar-hero.jpg",
    // Dedicated profile page — linked from the homepage Chief Guest section.
    profileUrl: "/usha-mangeshkar",
  },

  // ---- USHA MANGESHKAR — full profile, verified from the project's approved
  // research dossier (claude/chitti-ayee-hai-usha-mangeshkar-research-dossier.md).
  // Every fact below traces to that dossier's "Safe to Publish" list. Deliberately
  // excluded: her exact birthplace/birth date (disputed across sources), any
  // classical-training or honorary-doctorate claim, and any song/performance
  // claim specific to Chitti Ayee Hai itself — none of that has been confirmed.
  // Do NOT confuse with the unrelated singer Usha Uthup. Powers the dedicated
  // /usha-mangeshkar page. hasPhoto/photo are intentionally NOT duplicated here —
  // they live only on SITE.chiefGuest above; this page reads that object for its
  // Hero, same as the homepage Chapter 04 section, so both stay in sync
  // automatically once a real, approved photo is ever supplied. ----
  ushaMangeshkar: {
    intro:
      "Usha Mangeshkar is an Indian playback singer and the youngest of five children born to the Marathi theatre-musician Master Deenanath Mangeshkar and Shevanti Mangeshkar — making her a sister to Lata Mangeshkar, Asha Bhosle and Meena Khadikar, and elder sister to Hridaynath Mangeshkar. She began singing for Hindi films in 1954, but it was two decades later, with the devotional film Jai Santoshi Maa (1975), that her voice became a household name across India. Alongside her Hindi film work, she went on to build one of the most celebrated voices in Marathi cinema and lavani — a body of work many consider even more prominent than her Hindi catalogue. Over a career spanning decades, she has recorded across Hindi, Marathi, Gujarati and several other Indian languages, singing alongside some of the biggest names in Indian film music.",
    languages: {
      items: [
        {
          name: "Hindi",
          text: "An extensive, well-documented playback career in Hindi cinema from 1954 onward — including her breakthrough on Jai Santoshi Maa and her most iconic hit, Tu Mungda Mungda.",
        },
        {
          name: "Marathi",
          text: "Her most celebrated body of work — two decades of Marathi film songs and lavani, often regarded as her true creative home, and by many accounts even more prominent than her Hindi career.",
        },
        {
          name: "Gujarati",
          text: "A real, documented body of Gujarati film songs, recorded alongside artists such as Praful Dave.",
        },
      ],
      note: "Reputable sources also describe a career reaching into several other Indian languages over the decades — a genuine breadth, even where individual songs in every one of those languages haven't been independently verified for this profile.",
    },
    // 8 signature songs. `link`/`linkLabel` point only to a source verified for
    // that exact recording (Saregama, Apple Music, Spotify or JioSaavn) — where
    // no such source could be found (the Gujarati song below), link is left null
    // rather than inventing one. Never host or embed the audio itself.
    songs: [
      {
        title: "Tu Mungda Mungda",
        film: "Inkaar",
        year: "1977",
        language: "Hindi",
        note: "Her most iconic Hindi hit — a solo number composed by Rajesh Roshan.",
        link: "https://www.saregama.com/song/tu-mungada-mungada_80440",
        linkLabel: "Listen on Saregama",
      },
      {
        title: "Main To Aarti Utaroon Re Santoshi Mata Ki",
        film: "Jai Santoshi Maa",
        year: "1975",
        language: "Hindi · Devotional",
        note: "The devotional number that made her a household name overnight — her career breakthrough.",
        link: "https://open.spotify.com/track/2cfjCGusf2fCXLOpGN0TK1",
        linkLabel: "Listen on Spotify",
      },
      {
        title: "Mala Lagali Kunachi Uchaki",
        film: "Pinjara",
        year: "1972",
        language: "Marathi · Lavani",
        note: "Her signature lavani, and one of Marathi cinema's most celebrated songs.",
        link: "https://www.saregama.com/song/mala-lagali-kunachi-uchaki_12856",
        linkLabel: "Listen on Saregama",
      },
      {
        title: "Dhagala Lagali Kala",
        film: "Bot Lavin Tithe Gudgulya",
        year: "1978",
        language: "Marathi",
        coSinger: "With Mahendra Kapoor",
        note: "Her best-known mass-popularity crossover hit.",
        link: "https://www.jiosaavn.com/song/dagala-lagali-kala/Qg05AAx0XnE",
        linkLabel: "Listen on JioSaavn",
      },
      {
        title: "Madi Hu To Baar Baar Varshe Aavyo",
        film: "Patli Parmar",
        year: "1978",
        language: "Gujarati",
        coSinger: "With Praful Dave",
        note: "Part of her documented Gujarati film-song catalogue.",
        link: null,
      },
      {
        title: "Aplam Chaplam Chaplaayi Re",
        film: "Azaad",
        year: "1955",
        language: "Hindi",
        coSinger: "With Lata Mangeshkar",
        note: "A rare early-career duet with her elder sister.",
        link: "https://music.apple.com/us/song/aplam-chaplam/527014859",
        linkLabel: "Listen on Apple Music",
      },
      {
        title: "Chanda Mama Se Pyara Mera Mama",
        film: "Kartavya",
        year: "1979",
        language: "Hindi",
        coSinger: "With Mohammed Rafi",
        note: "A cherished duet with the legendary Mohammed Rafi.",
        link: "https://music.apple.com/ng/song/chanda-mama-se-pyara-mera-mama-from-kartavya/1504243514",
        linkLabel: "Listen on Apple Music",
      },
      {
        title: "Gorya Dehavarti",
        film: "Jait Re Jait",
        year: "1977",
        language: "Marathi",
        coSinger: "With Ravindra Sathe",
        note: "Composed by her own brother, Hridaynath Mangeshkar.",
        link: "https://www.saregama.com/song/gorya-dehavarti_20419",
        linkLabel: "Listen on Saregama",
      },
    ],
    // No individual video could be manually verified as belonging to an
    // official/authorized channel in this research pass — per the project's
    // safety rule, we do NOT embed any unverified video. Only the channel
    // itself, whose own About page explicitly self-identifies this way, is
    // linked. Revisit once specific videos are manually matched and confirmed.
    videos: {
      channelName: "Usha Mangeshkar",
      channelLabel: "Official Artist Channel",
      channelUrl: "https://www.youtube.com/channel/UCCJ7v34lgZ9TmJwFDkwfSxQ",
      note: "Her YouTube channel explicitly identifies itself as her official artist channel. We've chosen not to embed individual videos here until each one is manually verified against her confirmed discography — use the Listen links above, or explore her channel directly.",
    },
    // Verified collaborations only. No photo exists of Usha Mangeshkar together
    // with any of these artists — photo stays null for every entry, rendering
    // the same honest star/arch placeholder used on the Artist page's "In the
    // Company of Legends" section. Never substitute an unrelated celebrity photo.
    // Photos below are NOT photos of Usha Mangeshkar with these people — each
    // is a real, individually verified portrait of the named legend, used
    // purely to represent them visually on their own card, same convention as
    // the Artist page's "In the Company of Legends" section. A person with no
    // verified photo yet keeps photo: null, which renders the existing
    // star/arch placeholder — never an unverified image. Full source/license
    // record for every non-null entry: see
    // claude/chitti-ayee-hai-usha-mangeshkar-photo-sourcing-record.md
    legends: {
      intro: "Across a career spanning decades, she recorded alongside some of Indian music's most celebrated names.",
      items: [
        {
          name: "Lata Mangeshkar",
          note: "A rare early-career duet, “Aplam Chaplam Chaplaayi Re” (Azaad, 1955) — sung with her elder sister.",
          // Reused as-is from the Artist page's already-verified asset — same
          // file, same CC BY 3.0 / VRT #2008030310010794 license. No new
          // download needed.
          photo: "/artist/legends/legend-lata-mangeshkar.jpg",
        },
        {
          name: "Mohammed Rafi",
          note: "Duets including “Chanda Mama Se Pyara Mera Mama” (Kartavya, 1979) and the ensemble number “Janu Meri Jaan” (Shaan, 1980).",
          // Verified via Wikimedia Commons, Government of India / India Post
          // source, licensed GODL-India. See sourcing record doc.
          photo: "/usha-mangeshkar/legend-mohammed-rafi.jpg",
        },
        {
          name: "Hridaynath Mangeshkar",
          note: "Her younger brother composed “Gorya Dehavarti” for Jait Re Jait (1977).",
          // Verified via Wikimedia Commons — the same photo used as his
          // infobox portrait on English Wikipedia. CC BY-SA 3.0 / GFDL 1.2+.
          // See sourcing record doc.
          photo: "/usha-mangeshkar/legend-hridaynath-mangeshkar.jpg",
        },
        // Ram Kadam entry removed at founder's request (7 Sep 2026) — no
        // verified, properly licensed photograph could be found, and rather
        // than leave a photo-less placeholder card, the entry itself was
        // dropped. See claude/chitti-ayee-hai-usha-mangeshkar-photo-sourcing-record.md
        // for the sourcing attempt on record.
      ],
    },
    // Strongest verified awards only — do not add unconfirmed recognitions.
    awards: [
      { year: "2024", title: "Filmfare Marathi Lifetime Achievement Award" },
      {
        year: "2022",
        title: "Dr Bhupen Hazarika International Solidarity Award",
        detail: "Presented in Guwahati by the Governor of Assam.",
      },
    ],
    connection:
      "Long before she became a household name, Usha Mangeshkar's voice carried devotion straight into people's homes — the same spirit that fills a mehfil like Chitti Ayee Hai. Her presence on this stage is a quiet continuation of that tradition: a legendary voice honouring an evening built entirely around soulful, unhurried music.",
    sourcesNote:
      "Biographical and discography details are drawn from Wikipedia's Mangeshkar-family and film pages, Cinemaazi, and verified listings on Saregama, Apple Music, Spotify and JioSaavn, cross-checked wherever possible, plus award coverage from Filmfare and Sentinel Assam. Disputed details — her exact birthplace and birth date, claims of classical training, an honorary doctorate, and a small number of unresolved song credits — are deliberately left out pending further confirmation. Her participation in Chitti Ayee Hai reflects only what the event team has confirmed; see SITE.chiefGuest above.",
  },

  // ---- EVENT HIGHLIGHTS (exactly as presented on the official poster) ----
  highlights: [
    {
      icon: "music",
      title: "A Grand Qawwali Troupe",
      text: "The evening is led by a grand qawwali troupe — live voices, harmonium, tabla and chorus in true mehfil style.",
    },
    {
      icon: "sparkles",
      title: "Sufi, Ghazal & Qawwali Ensemble",
      text: "Beloved qawwalis, ghazals and taranas performed the way they were meant to be heard — unhurried and alive.",
    },
    {
      icon: "scroll",
      title: "Vintage Poetry & The Living Letter",
      text: "A special Chitti reading — the letter at the heart of the evening, brought to life on stage.",
    },
    {
      icon: "camera",
      title: "Themed Photo-Op & Selfie Zones",
      text: "Beautifully themed corners, so you can take a little of the evening home with you.",
    },
    {
      icon: "palette",
      title: "Art Exhibition & Sale",
      text: "Arrive early — the art exhibition and sale opens at 5:00 PM, before the show.",
    },
    {
      icon: "crown",
      title: "Celebrity Presence & Special Guests",
      text: "Special guests join the mehfil — announced by the organizers on our official channels.",
    },
  ],

  // ---- TICKET CATEGORIES (verified 6 Sept 2026) ----
  // Copied directly from backend/ticket_catalogue.py — the single approved
  // source of truth (sku, name, price_inr) — not re-typed from memory or from
  // any other doc. Deliberately NO "description"/benefit copy per tier: that
  // file's own docstring states per-tier inclusions have never been approved
  // ("a benefit that was never actually approved anywhere" — referring to an
  // earlier, incorrect "Early Bird" label that was removed for the same
  // reason). Do not add descriptions, seat counts or availability claims here
  // until those are separately confirmed. `topTier` only marks Mehfil-e-Khas
  // as the highest tier for a visual (not copy) VIP treatment on the homepage
  // — it is already documented elsewhere in the project as the top
  // purchasable tier, not a new claim.
  ticketCategories: [
    { id: "bronze", name: "Bronze", price: 599 },
    { id: "silver", name: "Silver", price: 999 },
    { id: "gold", name: "Gold", price: 1499 },
    { id: "platinum", name: "Platinum", price: 2199 },
    { id: "mehfil-e-khas", name: "Mehfil-e-Khas", price: 2999, topTier: true },
  ],

  // ---- SPONSORSHIP (official packages & inventory) ----
  sponsorPackages: [
    { id: "title-partner", name: "Title Partner", price: "₹10,50,000" },
    { id: "associate-partner", name: "Associate Partner", price: "₹7,50,000" },
    { id: "cultural-partner", name: "Cultural Partner", price: "₹2,75,000" },
  ],
  sponsorBenefits: [
    "Branding",
    "Stage & backdrop presence",
    "Venue branding",
    "Social media features",
    "Promotional reels & videos",
    "Brand activation",
    "Product / service showcase",
    "Emcee mentions",
    "VIP invitations",
    "Premium seating",
    "Category exclusivity",
    "Post-event acknowledgement",
  ],

  // ---- FAQ (verified answers only) ----
  faqs: [
    {
      q: "What is Chitti Ayee Hai?",
      a: "A soulful evening of Ghazal, Qawwali and Sufi music — a vintage-themed royal mehfil with a live ensemble, a special Chitti reading, themed photo zones and an art exhibition, at Dr. B.R. Ambedkar Bhavan, Bengaluru. Event by House of Bengaluru.",
    },
    {
      q: "Who is performing?",
      a: "Dr. Kaleem Pasha — celebrated for his Ghazal, Qawwali and Sufi performances — headlines the evening. See the Artist page for his full profile, music and career highlights. The full lineup will be revealed by the organizers closer to the event.",
    },
    {
      q: "When is the event?",
      a: "Saturday, 19 September 2026. The art exhibition opens at 5:00 PM and the show begins at 7:00 PM sharp.",
    },
    {
      q: "Where is it?",
      a: "At Dr. B.R. Ambedkar Bhavan, Vasanth Nagar, Bengaluru — a hall of approximately 1,254 seats. Use the “Get Directions” button in the Venue section to open it in Google Maps.",
    },
    {
      q: "What time does the exhibition start?",
      a: "The art exhibition and sale opens at 5:00 PM onwards, before the show.",
    },
    {
      q: "When does ticket scanning begin?",
      a: "Ticket scanning and the box office open at 6:00 PM onwards.",
    },
    {
      q: "When does auditorium entry begin?",
      a: "Auditorium entry begins at 6:30 PM onwards.",
    },
    {
      q: "When does the show start?",
      a: "The show begins at 7:00 PM sharp and runs for 3 hours. We recommend arriving early — entry begins 6:30 PM.",
    },
    {
      q: "Is it a seated event?",
      a: "Yes — it is a fully seated auditorium event. Seating within each ticket category is first-come, first-served, so arriving early gets you the best seats in your category.",
    },
    {
      q: "How do I book tickets?",
      a: "Tap any “Tickets Are Live” button on this page and you will be taken to the official BookMyShow listing to complete your purchase securely.",
    },
    {
      q: "Where do I buy tickets?",
      a: "Exclusively on BookMyShow, the official ticketing partner. Tickets start from ₹599. This website does not sell tickets directly.",
    },
    {
      q: "What is the age limit?",
      a: "The event is open to ages 10 and above.",
    },
    {
      q: "Is parking available?",
      a: "Yes, parking is available at the venue.",
    },
    {
      q: "What languages is the event in?",
      a: "The evening features performances in Hindi, Urdu and Kannada.",
    },
  ],

  // ---- EDITORIAL MARQUEE ribbon words ----
  marquee: ["Ghazal", "Qawwali", "Sufi", "19 September 2026", "Bengaluru"],
};
